import type { Properties } from '@mend/types/properties';
import type { Role } from '@mend/types/user';

/**
 * Check that the current user has permission to access insights based on their role
 * and the organization's signal settings.
 *
 * @param role - The user's role
 * @param properties - The organization properties
 * @returns boolean - Whether the user is allowed to access insights
 */
export function isUserAllowedInsights(
  role: Role,
  properties: Properties
): boolean {
  // Allow mend admin to bypass check
  if (role === 'Admin') {
    return true;
  }

  // Check if signal is enabled globally
  if (properties['settings.signal.enabled'] !== 1) {
    return false;
  }

  // Role property mapping based on PHP equivalent
  const rolePropertyMap: Partial<Record<Role, string>> = {
    Staff: 'settings.signal.staffEnabled',
    Provider: 'settings.signal.providerEnabled',
    'Provider Admin': 'settings.signal.providerAdminEnabled',
    'Org Admin': 'settings.signal.orgAdminEnabled',
  };

  // Check if role exists in map and has permission
  if (!rolePropertyMap[role]) {
    return false;
  }

  const roleProperty = rolePropertyMap[role];
  if (!roleProperty) return false;
  return properties[roleProperty] === 1;
}
